<template>
  <div style="overflow: visible" :style="{ height: windowHeight }">
    <!-- Show Post list button when viewport width is less than md breakpoint -->
    <div v-if="isXsSm" class="sticky-top" :class="{ 'btn__post-list': showList }" style="top: 1%">
      <b-button
        :variant="classPostsListButton"
        class="mb-3 btn__open-post-list"
        @click="handleClickShowList"
      >
        <span class="btn-wrapper--icon">
          <font-awesome-icon icon="bars" />
        </span>
        <span class="btn-wrapper--label">Posts list</span>
      </b-button>

      <!-- Posts list when click Posts list button -->
      <div v-if="showList" class="list__post-list list__post-list--overlap border border-medium">
        <div class="font-weight-bold mb-3">Posts list</div>
        <div v-for="(post, index) in posts" :key="index">
          <div class="d-flex text__post-name align-items-end" @click="handleClickPost(post.id)">
            <div v-if="!isOwner" class="font-size-md mr-2" :class="styleClass(post)">
              <font-awesome-icon
                v-if="isPostMarked(post)"
                :icon="{ prefix: 'fas', iconName: 'check' }"
                class="text-success mt-1 icon__computed-mark"
              />
              <font-awesome-icon
                v-else
                :icon="{ prefix: 'far', iconName: 'circle' }"
                size="sm"
                class="icon__incompleted-mark"
              />
            </div>
            <div :title="post.description" class="text__post-name pointer filter-text mt-3">
              {{ getPostTitle(post) }}
            </div>
          </div>
        </div>
        <div
          v-if="!isOwner && isModeLearnVerify"
          class="mt-3 cursor-pointer"
          @click="handleClickVerify"
        >
          Verify Competency
        </div>
      </div>
    </div>

    <!-- Show all Post lists when viewport width is equal or more than md breakpoint -->
    <div v-else class="sticky-top list__post-list" style="top: 5%">
      <div class="font-weight-bold mb-3">Posts list</div>
      <div v-for="(post, index) in posts" :key="index" class="ml-3">
        <div class="d-flex text__post-name align-items-end" @click="handleClickPost(post.id)">
          <div v-if="!isOwner" class="font-size-md mr-2" :class="styleClass(post)">
            <font-awesome-icon
              v-if="isPostMarked(post)"
              :icon="{ prefix: 'fas', iconName: 'check' }"
              class="text-success mt-1 icon__computed-mark"
            />

            <font-awesome-icon
              v-else
              :icon="{ prefix: 'far', iconName: 'circle' }"
              size="sm"
              class="icon__incompleted-mark"
            />
          </div>
          <div class="text__post-name pointer filter-text mt-3">
            {{ getPostTitle(post) }}
          </div>
        </div>
      </div>
      <div
        v-if="!isOwner && isModeLearnVerify"
        class="ml-3 mt-3 cursor-pointer"
        @click="handleClickVerify"
      >
        Verify Competency
      </div>
    </div>
  </div>
</template>

<script>
import PostMark from '@/models/PostMark';
import { debounce } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { commonStyleDescriptionMixin } from '@/mixins/commonStyleDescriptionMixin';
import { userInfoMixin } from '@/mixins/userInfoMixin';
import { datetimeMixin } from '@/mixins/datetimeMixin';
import Application from '@/models/Application';

import { library } from '@fortawesome/fontawesome-svg-core';
import { faCircle } from '@fortawesome/free-regular-svg-icons';

library.add(faCircle);

const breakpointAtMedium = 753;

export default {
  name: 'PostList',

  components: { FontAwesomeIcon },

  mixins: [datetimeMixin, commonStyleDescriptionMixin, userInfoMixin],

  props: {
    posts: {
      type: Array,
      default: () => []
    },
    isOwner: {
      type: Boolean
    }
  },

  data() {
    return {
      windowSize: null,
      showList: false
    };
  },

  computed: {
    learningUnitId() {
      return this.$route.params.learningUnitId || null;
    },

    isXsSm() {
      return this.windowSize < breakpointAtMedium;
    },

    windowHeight() {
      return this.isXsSm ? `${window.innerHeight + window.outerHeight}%` : '100%';
    },

    classPostsListButton() {
      return this.showList ? 'primary' : 'outline-primary';
    },

    applied() {
      return (
        Application.query()
          .where((apply) => {
            return apply.user.id == this.userId && apply.learning_unit.id == this.learningUnitId;
          })
          .first() || null
      );
    },

    isModeLearnVerify() {
      return this.applied ? this.applied.isModeLearnVerify : false;
    }
  },

  mounted() {
    this.registerWindowSizeObserver();
  },

  updated() {
    this.commonIdAttributes('description');
  },

  methods: {
    isPostMarked(post) {
      return this.user && post ? PostMark.isUserMarkedPost(post.id, this.user.id) : false;
    },

    styleClass(post) {
      return this.isPostMarked(post) ? 'icon__completed-mark' : 'icon__incompleted-mark';
    },

    filterText(text) {
      if (text) {
        const textLimit = this.isXsSm ? 50 : 20;

        if (text.length > textLimit) return text.substring(0, textLimit) + '...';
      }

      return text;
    },

    handleClickShowList() {
      this.showList = !this.showList;
    },

    handleClickPost(postId) {
      this.$emit('clickPost', postId);
    },

    handleClickVerify() {
      this.$emit('click:verify');
    },

    updateWindowSize(entries) {
      const [entry] = entries;
      this.windowSize = entry.contentRect.width;
    },

    registerWindowSizeObserver() {
      const callback = debounce(this.updateWindowSize, 100);

      const body = document.querySelector('body');
      const postResizeObserver = new ResizeObserver(callback);

      postResizeObserver.observe(body);
    },

    getPostTitle(post) {
      let { title = '' } = post;

      return title;
    }
  }
};
</script>

<style lang="scss" scoped>
.btn__open-post-list {
  background-color: var(--white);
  color: var(--primary);

  &:active,
  &:focus,
  &:hover {
    background-color: var(--primary);
    color: var(--white);
  }
}
// .btn__post-list {
//   border-radius: 10px;
//   box-shadow: 0 1rem 1rem rgba(0, 0, 0, 0.2);
// }

.list__post-list--overlap {
  position: absolute;
  top: 3rem;
  left: 0rem;
  padding: 1.5rem 1.5rem;
  width: 100%;
  background: white;
  border-radius: 10px;
  overflow-y: auto;
  box-shadow: 0 1rem 1rem rgba(126, 90, 90, 0.3);
}

.filter-text {
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
